import React from 'react';
import { Grid, Paper, Typography } from '@mui/material';

// Винесено з pages/expert/ClientPage.tsx - спільне для Експерта (profile tab) і Продакта
// (PmClientProfileTab) рендерення секції "Загальна/Системна/..." інформація, без змін логіки.
const DetailItem: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
  <Grid item xs={12} sm={6} md={4}>
    <Typography variant="body2" color="text.secondary">{label}</Typography>
    <Typography variant="body1">{value || 'Не вказано'}</Typography>
  </Grid>
);

// general.activityLevel/goal - фіксовані коди з онбордингу (api/src/common/enums/activity-level.enum.ts,
// goal.enum.ts), а не вільний текст; без цього в профілі показувався сирий код ("lightly_active").
// Лише uk - решта екрану теж захардкоджена українською (web i18n не підключено для цих сторінок).
const ACTIVITY_LEVEL_LABELS_UK: Record<string, string> = {
  sedentary: 'Сидячий спосіб життя',
  lightly_active: 'Легка активність (1-3 рази на тиждень)',
  moderately_active: 'Помірна активність (3-5 разів на тиждень)',
  very_active: 'Активний спосіб життя (6-7 разів на тиждень)',
  extra_active: 'Дуже активний (декілька разів на день)',
};

const GOAL_LABELS_UK: Record<string, string> = {
  weight_loss: 'Схуднення',
  health_support: 'Підтримка здоров’я',
  wellbeing_improvement: 'Покращення самопочуття',
  muscle_building: 'Нарощування м’язів',
  endurance: 'Витривалість',
  weight_gain: 'Набір ваги',
};

// fieldLabels - підписи динамічних nutrition/medical/social полів з бекенду (ClientProfileDataDto.
// fieldLabels, за field_key з адмін-редагованої nest_profile_fields - не фіксований DTO, тож без
// цього сирий snake_case ключ ("favorite_products_dishes") йшов прямо в UI).
export const renderClientProfileSection = (
  title: string,
  data: Record<string, any>,
  fieldLabels?: Record<string, string>,
) => {
  if (!data || Object.keys(data).length === 0) return null;

  return (
    <Paper sx={{ p: 3, mt: 3 }}>
      <Typography variant="h6" gutterBottom>{title}</Typography>
      <Grid container spacing={2}>
        {Object.entries(data).map(([key, value]) => {
          if (title === 'Загальна інформація' && key === 'avatar') return null;

          let displayValue = value;
          let displayKey = fieldLabels?.[key]
            ?? key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase());

          if (key === 'gender') {
            displayValue = value === 1 ? 'Чоловіча' : value === 2 ? 'Жіноча' : 'Не вказано';
            displayKey = 'Стать';
          } else if (key === 'createdAt' || key === 'lastLoginAt') {
            displayValue = value ? new Date(value).toLocaleDateString() : 'Не вказано';
            displayKey = key === 'createdAt' ? 'Дата створення' : 'Останній вхід';
          } else if (Array.isArray(value)) {
            displayValue = value.join(', ');
          } else if (key === 'email') {
            displayKey = 'Email';
          } else if (key === 'name') {
            displayKey = "Ім'я";
          } else if (key === 'surname') {
            displayKey = 'Прізвище';
          } else if (key === 'patronymic') {
            displayKey = 'По батькові';
          } else if (key === 'phone') {
            displayKey = 'Телефон';
          } else if (key === 'bornYear') {
            displayKey = 'Рік народження';
          } else if (key === 'height') {
            displayKey = 'Зріст (см)';
          } else if (key === 'activityLevel') {
            displayKey = 'Рівень активності';
            displayValue = typeof value === 'string' ? (ACTIVITY_LEVEL_LABELS_UK[value] ?? value) : value;
          } else if (key === 'goal') {
            displayKey = 'Ціль';
            displayValue = typeof value === 'string' ? (GOAL_LABELS_UK[value] ?? value) : value;
          } else if (key === 'timezone') {
            displayKey = 'Часовий пояс';
          } else if (key === 'inviteCode') {
            displayKey = 'Код запрошення';
          } else if (key === 'isActive') {
            displayKey = 'Активний';
            displayValue = value ? 'Так' : 'Ні';
          } else if (key === 'roles') {
            displayKey = 'Ролі';
          } else if (key === 'roleLabels') {
            displayKey = 'Назви ролей';
          } else if (key === 'permissions') {
            displayKey = 'Дозволи';
          } else if (key === 'isEmailVerified') {
            displayKey = 'Email підтверджено';
            displayValue = value ? 'Так' : 'Ні';
          } else if (key === 'isFirstSurveyCompleted') {
            displayKey = 'Перше опитування завершено';
            displayValue = value ? 'Так' : 'Ні';
          } else if (key === 'dietary_restrictions') {
            displayKey = 'Дієтичні обмеження';
          } else if (key === 'favorite_products') {
            displayKey = 'Улюблені продукти';
          } else if (key === 'favorite_dishes') {
            displayKey = 'Улюблені страви';
          } else if (key === 'disliked_food') {
            displayKey = 'Небажані продукти';
          } else if (key === 'meals_count') {
            displayKey = 'Прийомів їжі на день';
          } else if (key === 'snacks_count') {
            displayKey = 'Перекусів на день';
          } else if (key === 'daily_liquid_liters') {
            displayKey = 'Рідини на день (л)';
          } else if (key === 'daily_water_liters') {
            displayKey = 'Води на день (л)';
          } else if (key === 'complaints') {
            displayKey = 'Скарги';
          } else if (key === 'chronic_diseases') {
            displayKey = 'Хронічні захворювання';
          } else if (key === 'family_chronic_diseases') {
            displayKey = 'Спадкові хронічні захворювання';
          } else if (key === 'surgeries_injuries') {
            displayKey = 'Операції та травми';
          } else if (key.startsWith('client_')) {
            displayKey = key.replace('client_', '').charAt(0).toUpperCase() + key.replace('client_', '').slice(1);
          }

          return <DetailItem key={key} label={displayKey} value={displayValue} />;
        })}
      </Grid>
    </Paper>
  );
};
